import { useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Bot } from "lucide-react";
import { toast } from "sonner";

export const SpecialCategoryConsent = ({ onGranted }) => {
  const { token } = useAuth();
  const [busy, setBusy] = useState(false);
  const grant = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/consent/special-category`, { granted: true }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Consent recorded. Thank you.");
      onGranted?.();
    } catch {
      toast.error("Failed to record consent");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card-omni p-8 max-w-xl mx-auto mt-10" data-testid="special-category-consent-card">
      <div className="flex items-center gap-3 mb-4">
        <ShieldCheck className="w-6 h-6 text-primary" strokeWidth={1.5} />
        <h2 className="font-heading text-xl font-medium">Your consent for wellbeing data</h2>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed mb-3">
        This feature processes <strong className="text-foreground">special category data</strong> — information about your
        emotional state, mental wellbeing, and personal beliefs. Under GDPR Article 9, UK GDPR, PIPEDA (Canada) and
        CPRA (California), we need your <strong className="text-foreground">explicit consent</strong> before processing it.
      </p>
      <ul className="text-sm text-muted-foreground space-y-1.5 mb-4 list-disc list-inside">
        <li>Used only to power your mood journal, insights and therapy paths</li>
        <li>Never sold or shared with advertisers</li>
        <li>Deleted completely if you delete your account</li>
        <li>You can withdraw consent anytime in Settings — features simply pause</li>
      </ul>
      <div className="flex gap-3">
        <Button data-testid="grant-special-consent-btn" onClick={grant} disabled={busy} className="btn-omni bg-primary text-primary-foreground rounded-full">
          {busy ? "Recording..." : "I Consent"}
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground/70 mt-4">
        Consent version 2026-06 · Recorded with timestamp · See our Privacy Policy for details.
      </p>
    </div>
  );
};

export const AIDisclosureNotice = ({ module, onAck, busy }) => (
  <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-6" data-testid="ai-disclosure-modal">
    <div className="card-omni bg-background p-8 max-w-md w-full">
      <div className="flex items-center gap-3 mb-4">
        <Bot className="w-6 h-6 text-primary" strokeWidth={1.5} />
        <h2 className="font-heading text-lg font-medium">You're talking to an AI</h2>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed mb-3">
        The <span className="capitalize font-medium text-foreground">{module?.replace(/_/g, " ")}</span> persona is an
        artificial intelligence system. Its responses are AI-generated — not from a human, and not from a licensed professional.
      </p>
      <p className="text-sm text-muted-foreground leading-relaxed mb-5">
        Omni is not a substitute for professional medical, psychological, legal, or financial advice.
        In a crisis, contact local emergency services.
      </p>
      <Button data-testid="ai-disclosure-ack-btn" onClick={onAck} disabled={busy} className="w-full btn-omni bg-primary text-primary-foreground rounded-full">
        I Understand
      </Button>
      <p className="text-[10px] text-muted-foreground/60 mt-3 text-center">EU AI Act Article 13 transparency notice · logged for compliance</p>
    </div>
  </div>
);
